import {
  huddlePollResultSchema,
  pollSharePercent,
  pollWinners,
  type Message,
} from '@avslack/shared';
import { formatTime } from '../lib/format';
import { Icon } from './Icon';

/**
 * Az olelesben tartott szavazas EREDMENYE a csatorna szovegfolyamaban.
 *
 * A `meta.result` szerkezetes (kerdes, megoszlas); ha valamiert nem olvashato, a
 * szoveg-valtozatot mutatjuk (az mindig ott van az uzenetben).
 */
export function HuddlePollResultRow({ message }: { message: Message }) {
  const parsed = huddlePollResultSchema.safeParse(message.meta?.result);

  if (!parsed.success) {
    return (
      <div className="px-3.5 py-1.5 text-xs text-ink-faint">
        <span className="whitespace-pre-line">{message.text}</span>
      </div>
    );
  }

  const result = parsed.data;
  const winners = pollWinners(result);

  return (
    <div className="px-3.5 py-2">
      <section
        aria-label="Szavazás eredménye"
        className="max-w-md rounded-card border border-border-soft bg-surface-raised px-4 py-3 shadow-(--shadow-soft)"
      >
        <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
          <span aria-hidden className="text-accent">
            <Icon name="csillag" size="sm" />
          </span>
          Szavazás az ölelésben
          <time className="ml-auto font-normal normal-case" dateTime={message.createdAt}>
            {formatTime(message.createdAt)}
          </time>
        </p>
        <p className="mt-1 text-sm font-semibold text-ink">{result.question}</p>

        <ul className="mt-2 space-y-1.5">
          {result.options.map((option, index) => {
            const share = pollSharePercent(option.votes, result.voterCount);
            const won = winners.includes(option.text);
            return (
              <li
                key={index}
                className="relative overflow-hidden rounded-control border border-border-soft px-3 py-1.5 text-sm"
              >
                <span
                  aria-hidden
                  className={`absolute inset-y-0 left-0 ${won ? 'bg-accent-soft' : 'bg-surface-sunken'}`}
                  style={{ width: `${share}%` }}
                />
                <span className="relative flex items-center gap-2">
                  <span className={`min-w-0 flex-1 truncate ${won ? 'font-semibold' : ''}`}>
                    {won && <span aria-hidden>🏆 </span>}
                    {option.text}
                  </span>
                  <span className="shrink-0 text-2xs tabular-nums text-ink-muted">
                    {option.votes} · {share}%
                  </span>
                </span>
              </li>
            );
          })}
        </ul>

        <p className="mt-2 text-2xs text-ink-faint">
          {result.voterCount === 0 ? 'Senki nem szavazott.' : `${result.voterCount} fő szavazott`}
          {result.anonymous ? ' · névtelen' : ''}
          {result.multiple ? ' · több válasz adható' : ''}
        </p>
      </section>
    </div>
  );
}
